import React, { useMemo } from 'react';
import { Box, Flex, Grid, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import { formatCurrency } from '../../../../../utils/FormatFunctions';
import { num } from '../../format';
import { IPessoalEmpregado, IPessoalHeadcount } from '../../types';
import { UI } from '../../ui';
import { classifyCargo, genderKey, JobArchetype } from './jobArchetypes';
import { statusOf } from './CollabAvatar';
import { anosDeCasa } from './CollabCard';

/* --------------------------- agregação client-side --------------------------- */

interface ArchAgg {
  arch: JobArchetype;
  n: number;
  m: number;
  f: number;
  somaSal: number;
  cntSal: number;
}
interface DeptoAgg {
  depto: string;
  n: number;
  somaSal: number;
  cntSal: number;
}

const FAIXAS = [
  { label: '< 1 ano', min: 0, max: 1 },
  { label: '1–3 anos', min: 1, max: 3 },
  { label: '3–5 anos', min: 3, max: 5 },
  { label: '5–10 anos', min: 5, max: 10 },
  { label: '10+ anos', min: 10, max: Infinity },
];

function useAnalytics(empregados: IPessoalEmpregado[]) {
  return useMemo(() => {
    const archMap = new Map<string, ArchAgg>();
    const deptoMap = new Map<string, DeptoAgg>();
    const tempo = FAIXAS.map(faixa => ({ ...faixa, qtd: 0 }));
    let somaSalGeral = 0;
    let cntSalGeral = 0;

    empregados.forEach(e => {
      const a = classifyCargo(e.cargo);
      const g = genderKey(e.sexo);
      const sal = num(e.salario);
      const temSal = sal > 0;

      const ag = archMap.get(a.key) ?? { arch: a, n: 0, m: 0, f: 0, somaSal: 0, cntSal: 0 };
      ag.n += 1;
      if (g === 'M') ag.m += 1;
      else if (g === 'F') ag.f += 1;
      if (temSal) {
        ag.somaSal += sal;
        ag.cntSal += 1;
      }
      archMap.set(a.key, ag);

      const dnome = (e.departamento || 'Sem departamento').trim();
      const dg = deptoMap.get(dnome) ?? { depto: dnome, n: 0, somaSal: 0, cntSal: 0 };
      dg.n += 1;
      if (temSal) {
        dg.somaSal += sal;
        dg.cntSal += 1;
      }
      deptoMap.set(dnome, dg);

      if (temSal) {
        somaSalGeral += sal;
        cntSalGeral += 1;
      }

      // só conta tempo de casa de quem está ativo
      if (statusOf(e.status) === 'ativo') {
        const anos = anosDeCasa(e.admissao);
        const faixa = tempo.find(fax => anos >= fax.min && anos < fax.max);
        if (faixa) faixa.qtd += 1;
      }
    });

    const archs = Array.from(archMap.values()).sort((x, y) => y.n - x.n);
    const deptos = Array.from(deptoMap.values())
      .sort((x, y) => y.n - x.n)
      .slice(0, 8);
    const salMedioGeral = cntSalGeral ? somaSalGeral / cntSalGeral : 0;
    return { archs, deptos, tempo, salMedioGeral };
  }, [empregados]);
}

/* ------------------------------- UI helpers ------------------------------- */

const GlassPanel = ({
  title,
  sub,
  children,
}: {
  title: string;
  sub?: string;
  children: React.ReactNode;
}) => (
  <Box
    position="relative"
    overflow="hidden"
    borderRadius="18px"
    p={5}
    bg="rgba(255,255,255,0.7)"
    backdropFilter="blur(14px) saturate(160%)"
    border="1px solid rgba(255,255,255,0.85)"
    boxShadow="0 12px 34px -18px rgba(15,23,42,0.35)"
  >
    <Flex justify="space-between" align="baseline" mb={4} gap={2}>
      <Text fontSize="13px" fontWeight={800} color={UI.ink}>
        {title}
      </Text>
      {sub && (
        <Text fontSize="11px" color={UI.faint} fontWeight={600}>
          {sub}
        </Text>
      )}
    </Flex>
    {children}
  </Box>
);

/* ------------------------------- componente ------------------------------- */

interface PeopleAnalyticsProps {
  empregados: IPessoalEmpregado[];
  serie?: IPessoalHeadcount[];
}

export function PeopleAnalytics({ empregados, serie }: PeopleAnalyticsProps) {
  const { archs, deptos, tempo, salMedioGeral } = useAnalytics(empregados);

  // Coerção decimal-string → número (headcount/turnover somem do gráfico se string).
  const serieChart = useMemo(
    () =>
      (serie ?? []).map(s => ({
        ...s,
        headcount: num(s.headcount),
        turnover_pct: num(s.turnover_pct),
      })),
    [serie],
  );

  const maxArch = Math.max(...archs.map(a => a.n), 1);
  const maxSal = Math.max(...archs.map(a => (a.cntSal ? a.somaSal / a.cntSal : 0)), 1);
  const maxGen = Math.max(...archs.slice(0, 8).map(a => Math.max(a.m, a.f)), 1);
  const maxTempo = Math.max(...tempo.map(t => t.qtd), 1);
  const maxDepto = Math.max(...deptos.map(d => d.n), 1);

  if (empregados.length === 0) {
    return (
      <Box textAlign="center" py={16} color={UI.muted}>
        Sem colaboradores para analisar.
      </Box>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
    >
      {/* 1 — Mapa da equipe por trabalho */}
      <GlassPanel title="Mapa da equipe por trabalho" sub={`${archs.length} áreas`}>
        <Box>
          {archs.map(a => {
            const { Icon } = a.arch;
            const media = a.cntSal ? a.somaSal / a.cntSal : 0;
            return (
              <Flex key={a.arch.key} align="center" gap={3} mb={2.5}>
                <Flex
                  align="center"
                  justify="center"
                  w="30px"
                  h="30px"
                  borderRadius="9px"
                  bgGradient={`linear(to-br, ${a.arch.gradient[0]}, ${a.arch.gradient[1]})`}
                  color="white"
                  flexShrink={0}
                  boxShadow={`0 4px 10px -3px ${a.arch.accent}80`}
                >
                  <Icon size={16} weight="fill" />
                </Flex>
                <Text
                  w="118px"
                  fontSize="12px"
                  fontWeight={700}
                  color={UI.ink}
                  noOfLines={1}
                  flexShrink={0}
                >
                  {a.arch.label}
                </Text>
                <Box
                  flex={1}
                  h="9px"
                  borderRadius="full"
                  bg="rgba(15,23,42,0.06)"
                  overflow="hidden"
                >
                  <Box
                    h="100%"
                    borderRadius="full"
                    bgGradient={`linear(to-r, ${a.arch.gradient[0]}, ${a.arch.gradient[1]})`}
                    w={`${(a.n / maxArch) * 100}%`}
                  />
                </Box>
                <Text
                  w="34px"
                  textAlign="right"
                  fontSize="13px"
                  fontWeight={800}
                  color={UI.ink}
                  flexShrink={0}
                >
                  {a.n}
                </Text>
                <Text
                  w="92px"
                  textAlign="right"
                  fontSize="11.5px"
                  fontWeight={600}
                  color={UI.muted}
                  flexShrink={0}
                >
                  {media > 0 ? formatCurrency(media) : '—'}
                </Text>
              </Flex>
            );
          })}
        </Box>
      </GlassPanel>

      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={5} mt={5}>
        {/* 2 — Gênero por trabalho (divergente) */}
        <GlassPanel title="Gênero por trabalho" sub="M · F">
          {archs.slice(0, 8).map(a => (
            <Flex key={a.arch.key} align="center" gap={2} mb={2.5}>
              <Text
                w="34px"
                textAlign="right"
                fontSize="11.5px"
                fontWeight={700}
                color="#2563EB"
                flexShrink={0}
              >
                {a.m}
              </Text>
              <Flex flex={1} h="10px" align="center" justify="flex-end" overflow="hidden">
                <Box
                  h="100%"
                  borderRadius="6px 0 0 6px"
                  bg="#3B82F6"
                  w={`${(a.m / maxGen) * 100}%`}
                />
              </Flex>
              <Text
                w="86px"
                textAlign="center"
                fontSize="10.5px"
                fontWeight={700}
                color={UI.muted}
                noOfLines={1}
                flexShrink={0}
              >
                {a.arch.label}
              </Text>
              <Flex flex={1} h="10px" align="center" justify="flex-start" overflow="hidden">
                <Box
                  h="100%"
                  borderRadius="0 6px 6px 0"
                  bg="#EC4899"
                  w={`${(a.f / maxGen) * 100}%`}
                />
              </Flex>
              <Text w="34px" fontSize="11.5px" fontWeight={700} color="#DB2777" flexShrink={0}>
                {a.f}
              </Text>
            </Flex>
          ))}
          <Flex justify="space-between" mt={3} px={1}>
            <Text fontSize="10.5px" fontWeight={700} color="#2563EB">
              Masculino
            </Text>
            <Text fontSize="10.5px" fontWeight={700} color="#DB2777">
              Feminino
            </Text>
          </Flex>
        </GlassPanel>

        {/* 3 — Faixa salarial por trabalho */}
        <GlassPanel
          title="Salário médio por trabalho"
          sub={`média geral ${formatCurrency(salMedioGeral)}`}
        >
          {archs
            .filter(a => a.cntSal > 0)
            .slice(0, 8)
            .map(a => {
              const media = a.somaSal / a.cntSal;
              return (
                <Flex key={a.arch.key} align="center" gap={3} mb={2.5}>
                  <Text
                    w="118px"
                    fontSize="12px"
                    fontWeight={700}
                    color={UI.ink}
                    noOfLines={1}
                    flexShrink={0}
                  >
                    {a.arch.label}
                  </Text>
                  <Box
                    flex={1}
                    h="9px"
                    borderRadius="full"
                    bg="rgba(15,23,42,0.06)"
                    overflow="hidden"
                  >
                    <Box
                      h="100%"
                      borderRadius="full"
                      bgGradient={`linear(to-r, ${a.arch.gradient[0]}, ${a.arch.gradient[1]})`}
                      w={`${(media / maxSal) * 100}%`}
                    />
                  </Box>
                  <Text
                    w="92px"
                    textAlign="right"
                    fontSize="12px"
                    fontWeight={800}
                    color={UI.ink}
                    flexShrink={0}
                  >
                    {formatCurrency(media)}
                  </Text>
                </Flex>
              );
            })}
        </GlassPanel>

        {/* 4 — Tempo de casa */}
        <GlassPanel title="Tempo de casa" sub="colaboradores ativos">
          {tempo.map(t => (
            <Flex key={t.label} align="center" gap={3} mb={2.5}>
              <Text w="80px" fontSize="12px" fontWeight={700} color={UI.muted} flexShrink={0}>
                {t.label}
              </Text>
              <Box flex={1} h="10px" borderRadius="full" bg="rgba(15,23,42,0.06)" overflow="hidden">
                <Box
                  h="100%"
                  borderRadius="full"
                  bgGradient={`linear(to-r, ${UI.brand}, #1AA6B7)`}
                  w={`${(t.qtd / maxTempo) * 100}%`}
                />
              </Box>
              <Text
                w="34px"
                textAlign="right"
                fontSize="13px"
                fontWeight={800}
                color={UI.ink}
                flexShrink={0}
              >
                {t.qtd}
              </Text>
            </Flex>
          ))}
        </GlassPanel>

        {/* 5 — Por departamento */}
        <GlassPanel title="Por departamento" sub="top 8 · efetivo e salário médio">
          {deptos.map(d => {
            const media = d.cntSal ? d.somaSal / d.cntSal : 0;
            return (
              <Flex key={d.depto} align="center" gap={3} mb={2.5}>
                <Text
                  w="118px"
                  fontSize="12px"
                  fontWeight={700}
                  color={UI.ink}
                  noOfLines={1}
                  flexShrink={0}
                >
                  {d.depto}
                </Text>
                <Box
                  flex={1}
                  h="9px"
                  borderRadius="full"
                  bg="rgba(15,23,42,0.06)"
                  overflow="hidden"
                >
                  <Box
                    h="100%"
                    borderRadius="full"
                    bgGradient={`linear(to-r, ${UI.pos}, #0E9F6E)`}
                    w={`${(d.n / maxDepto) * 100}%`}
                  />
                </Box>
                <Text
                  w="34px"
                  textAlign="right"
                  fontSize="13px"
                  fontWeight={800}
                  color={UI.ink}
                  flexShrink={0}
                >
                  {d.n}
                </Text>
                <Text
                  w="92px"
                  textAlign="right"
                  fontSize="11.5px"
                  fontWeight={600}
                  color={UI.muted}
                  flexShrink={0}
                >
                  {media > 0 ? formatCurrency(media) : '—'}
                </Text>
              </Flex>
            );
          })}
        </GlassPanel>
      </Grid>

      {/* 6 — Headcount × turnover */}
      <Box mt={5}>
        <GlassPanel title="Headcount × turnover" sub="evolução mensal">
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={serieChart}>
              <defs>
                <linearGradient id="hcFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={UI.brand} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={UI.brand} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={UI.border} />
              <XAxis dataKey="mes" fontSize={11} stroke={UI.muted} />
              <YAxis yAxisId="l" fontSize={11} stroke={UI.muted} allowDecimals={false} />
              <YAxis
                yAxisId="r"
                orientation="right"
                fontSize={11}
                stroke={UI.muted}
                tickFormatter={v => `${v}%`}
              />
              <Tooltip />
              <Legend />
              <Area
                yAxisId="l"
                type="monotone"
                dataKey="headcount"
                name="Headcount"
                stroke={UI.brand}
                strokeWidth={2}
                fill="url(#hcFill)"
              />
              <Line
                yAxisId="r"
                type="monotone"
                dataKey="turnover_pct"
                name="Turnover %"
                stroke={UI.neg}
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </GlassPanel>
      </Box>
    </motion.div>
  );
}
